"use client"
import { Box, SimpleGrid, Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import { type GridCardsBlock as GridCardsBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface GridCardsBlockProps extends ContentBlockTitleNumberProps {
  block: GridCardsBlockType
}

interface GridCard {
  content?: string
  preface?: string
  title?: string
}

type GridCardRenderItem =
  | {
      card: GridCard
      type: "card"
    }
  | {
      type: "placeholder"
    }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value
  }

  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

const getString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()

    return trimmedValue.length > 0 ? trimmedValue : undefined
  }

  if (typeof value === "number") {
    return String(value)
  }

  return undefined
}

const getPositiveInteger = (value: unknown): number | undefined => {
  const parsedValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value.trim())
        : Number.NaN

  if (!Number.isFinite(parsedValue)) {
    return undefined
  }

  const integerValue = Math.floor(parsedValue)

  return integerValue > 0 ? integerValue : undefined
}

const unwrapCardRecord = (
  item: Record<string, unknown>
): Record<string, unknown> => {
  const nestedKeys = [
    "item",
    "card",
    "card_id",
    "grid_card",
    "grid_card_id",
    "grid_cards_id",
  ]

  for (const key of nestedKeys) {
    const nestedValue = item[key]

    if (isRecord(nestedValue)) {
      return nestedValue
    }
  }

  return item
}

const normalizeGridCard = (item: unknown): GridCard | null => {
  const parsedItem = parseJsonValue(item)

  if (!isRecord(parsedItem)) {
    return null
  }

  const cardRecord = unwrapCardRecord(parsedItem)
  const preface =
    getString(cardRecord.preface) ??
    getString(cardRecord.eyebrow) ??
    getString(cardRecord.label)
  const title =
    getString(cardRecord.title) ??
    getString(cardRecord.heading) ??
    getString(cardRecord.value)
  const content =
    getString(cardRecord.content) ??
    getString(cardRecord.description) ??
    getString(cardRecord.copy) ??
    getString(cardRecord.text)

  if (!preface && !title && !content) {
    return null
  }

  return {
    content,
    preface,
    title,
  }
}

const normalizeGridCards = (value: unknown): GridCard[] => {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.reduce<GridCard[]>((cards, cardItem) => {
      const normalizedCard = normalizeGridCard(cardItem)

      if (normalizedCard !== null) {
        cards.push(normalizedCard)
      }

      return cards
    }, [])
  }

  if (isRecord(parsedValue)) {
    const nestedItems =
      parsedValue.cards ??
      parsedValue.items ??
      parsedValue.values ??
      parsedValue.grid_cards

    if (nestedItems) {
      return normalizeGridCards(nestedItems)
    }

    return Object.entries(parsedValue).reduce<GridCard[]>(
      (cards, [preface, cardValue]) => {
        const normalizedCard = normalizeGridCard({
          preface,
          title: cardValue,
        })

        if (normalizedCard !== null) {
          cards.push(normalizedCard)
        }

        return cards
      },
      []
    )
  }

  return []
}

const GridCardsBlock: FC<GridCardsBlockProps> = ({ block, titleNumber }) => {
  const {
    after_content,
    cards,
    columns_number,
    content,
    darker_bg,
    description,
    joint_cards,
    previous_content,
    title,
    two_columns_grid_cards,
  } = block
  const trimmedTitle = title?.trim()
  const trimmedDescription = description?.trim()
  const trimmedPreviousContent = previous_content?.trim() || content?.trim()
  const trimmedAfterContent = after_content?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription = Boolean(trimmedDescription)
  const hasPreviousContent = Boolean(trimmedPreviousContent)
  const hasAfterContent = Boolean(trimmedAfterContent)
  const normalizedCards = normalizeGridCards(cards)
  const isJoint = joint_cards !== false
  const configuredColumns = getPositiveInteger(columns_number) ?? 3
  const cardColumns = {
    base: 1,
    sm: Math.min(Math.max(normalizedCards.length, 1), configuredColumns),
  }
  const placeholderCount =
    isJoint && normalizedCards.length % cardColumns.sm !== 0
      ? cardColumns.sm - (normalizedCards.length % cardColumns.sm)
      : 0
  const gridItems: GridCardRenderItem[] = [
    ...normalizedCards.map((card) => ({ card, type: "card" as const })),
    ...Array.from({ length: placeholderCount }, () => ({
      type: "placeholder" as const,
    })),
  ]

  if (
    !hasTitle &&
    !hasDescription &&
    !hasPreviousContent &&
    !hasAfterContent &&
    normalizedCards.length === 0
  ) {
    return null
  }

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="grid-cards-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns_grid_cards ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_grid_cards ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: two_columns_grid_cards ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(Boolean(two_columns_grid_cards))}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={trimmedDescription}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        gap={6}
        maxWidth={{
          base: "100%",
          lg: two_columns_grid_cards ? "70%" : "100%",
        }}
        pl={!hasTitle && two_columns_grid_cards ? { base: 0, lg: 12 } : 0}
        w="full"
      >
        <BlurFade flexDirection="column" gap={8} inView w="full">
          {hasPreviousContent && (
            <MarkdownParser
              fontSize={{ base: "md", lg: "md" }}
              markdown={trimmedPreviousContent}
              pProps={{ color: "fg.muted" }}
            />
          )}
          {normalizedCards.length > 0 && (
            <Box
              border={isJoint ? "1px solid {colors.border}" : undefined}
              borderRadius="lg"
              my={0}
              overflow={isJoint ? "hidden" : "visible"}
              w="full"
            >
              <SimpleGrid
                columns={cardColumns}
                gap={isJoint ? 0 : 3}
                textAlign={isJoint ? "left" : "center"}
                w="full"
              >
                {gridItems.map((item, index) => {
                  const isFirstColumnAtSm = index % cardColumns.sm === 0
                  const isFirstRowAtSm = index < cardColumns.sm
                  const isPlaceholder = item.type === "placeholder"

                  return (
                    <VStack
                      aria-hidden={isPlaceholder ? true : undefined}
                      alignItems={isJoint ? "flex-start" : "center"}
                      border={isJoint ? undefined : "1px solid {colors.border}"}
                      borderLeft={
                        isJoint
                          ? {
                              base: "none",
                              sm: isFirstColumnAtSm
                                ? "none"
                                : "1px solid {colors.border}",
                            }
                          : undefined
                      }
                      borderTop={
                        isJoint
                          ? {
                              base:
                                index === 0
                                  ? "none"
                                  : "1px solid {colors.border}",
                              sm: isFirstRowAtSm
                                ? "none"
                                : "1px solid {colors.border}",
                            }
                          : undefined
                      }
                      className="grid-card"
                      display={
                        isPlaceholder ? { base: "none", sm: "flex" } : "flex"
                      }
                      gap={1.5}
                      key={
                        isPlaceholder
                          ? `placeholder-${index}`
                          : `${item.card.preface ?? "card"}-${item.card.title ?? index}`
                      }
                      minH={isJoint ? undefined : "64px"}
                      p={isJoint ? 5 : 4}
                      rounded={isJoint ? undefined : "lg"}
                      w="full"
                    >
                      {item.type === "card" && item.card.preface && (
                        <Text
                          color={{
                            base: "primary.500",
                            _dark: "primary.300",
                          }}
                          fontFamily="mono"
                          fontSize="xs"
                          fontWeight="medium"
                          letterSpacing="0.16em"
                          lineHeight="short"
                          textTransform="uppercase"
                        >
                          {item.card.preface}
                        </Text>
                      )}
                      {item.type === "card" && item.card.title && (
                        <Text
                          color="fg"
                          fontSize="15px"
                          fontWeight="medium"
                          lineHeight="short"
                        >
                          {item.card.title}
                        </Text>
                      )}
                      {item.type === "card" && item.card.content && (
                        <MarkdownParser
                          color="fg.muted"
                          fontSize={{ base: "md", lg: "md" }}
                          markdown={item.card.content}
                          pProps={{
                            textAlign: isJoint ? "left" : "center",
                          }}
                        />
                      )}
                    </VStack>
                  )
                })}
              </SimpleGrid>
            </Box>
          )}
          {hasAfterContent && (
            <MarkdownParser
              fontSize={{ base: "md", lg: "md" }}
              markdown={trimmedAfterContent}
              pProps={{ color: "fg.muted" }}
            />
          )}
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default GridCardsBlock
